import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Plus, Trash2, Pin, PinOff, Megaphone, Loader2, Pencil, X, Check
} from "lucide-react";
import BottomNav from "./BottomNav";
import { AppSidebar, SidebarMenuButton } from "./AppSidebar";

// Helper for authenticated API calls
const authFetch = async (url, options = {}) => {
  const storedToken = localStorage.getItem("session_token");
  const headers = {
    ...options.headers,
    ...(storedToken ? { "Authorization": `Bearer ${storedToken}` } : {}),
  };
  return fetch(url, { ...options, headers });
};

export default function NoticesPage() {
  const navigate = useNavigate();
  const API_URL = API;
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [pinned, setPinned] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const fetchNotices = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/notices`);
      if (res.ok) setNotices(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [API_URL]);

  useEffect(() => { fetchNotices(); }, [fetchNotices]);

  const sortedNotices = [...notices].sort((a, b) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  const handleSave = async () => {
    if (!title.trim()) { toast.error("Pealkiri on kohustuslik"); return; }
    setSaving(true);
    try {
      const url = editingId
        ? `${API_URL}/notices/${editingId}`
        : `${API_URL}/notices`;
      const res = await authFetch(url, {
        method: editingId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: title.trim(), text: text.trim(), pinned }),
      });
      if (res.ok) {
        toast.success(editingId ? "Teade uuendatud" : "Teade lisatud");
        resetForm();
        fetchNotices();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Salvestamine ebaõnnestus");
      }
    } catch (error) {
      toast.error("Salvestamine ebaõnnestus");
    } finally { setSaving(false); }
  };

  const startEdit = (notice) => {
    setEditingId(notice.notice_id);
    setTitle(notice.title);
    setText(notice.text || "");
    setPinned(notice.pinned || false);
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setTitle("");
    setText("");
    setPinned(false);
  };

  const deleteNotice = async (noticeId) => {
    if (!window.confirm("Kas olete kindel, et soovite selle teate kustutada?")) return;
    try {
      const res = await authFetch(`${API_URL}/notices/${noticeId}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Teade kustutatud");
        setNotices(prev => prev.filter(n => n.notice_id !== noticeId));
      }
    } catch (error) {
      toast.error("Kustutamine ebaõnnestus");
    }
  };

  const togglePin = async (notice) => {
    try {
      const res = await authFetch(`${API_URL}/notices/${notice.notice_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pinned: !notice.pinned }),
      });
      if (res.ok) fetchNotices();
    } catch (error) {
      toast.error("Uuendamine ebaõnnestus");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-16 md:pb-0" data-testid="notices-page">
      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="md:ml-64 min-h-screen">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
            <SidebarMenuButton onClick={() => setSidebarOpen(true)} />
            <h1 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Megaphone className="w-5 h-5" /> Teadetetahvel
            </h1>
            <Button
              onClick={() => showForm ? resetForm() : setShowForm(true)}
              size="sm"
              className="ml-auto bg-slate-900 hover:bg-slate-800"
              data-testid="new-notice-btn"
            >
              {showForm ? <X className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
              {showForm ? "Tühista" : "Lisa teade"}
            </Button>
          </div>
        </header>

      <main className="max-w-4xl mx-auto p-4 md:p-6 space-y-4">
        {/* New/Edit form */}
        {showForm && (
          <Card className="border-slate-200 border-l-4 border-l-amber-400" data-testid="notice-form">
            <CardContent className="p-4 space-y-3">
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Pealkiri *"
                className="font-medium"
                data-testid="notice-title-input"
              />
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Sisu (valikuline)"
                className="w-full min-h-[80px] rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 resize-none"
                data-testid="notice-text-input"
              />
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setPinned(!pinned)}
                  className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full transition-colors ${
                    pinned ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"
                  }`}
                  data-testid="notice-pin-toggle"
                >
                  <Pin className="w-3.5 h-3.5" /> {pinned ? "Kinnitatud" : "Kinnita üles"}
                </button>
                <Button onClick={handleSave} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="notice-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                  {editingId ? "Uuenda" : "Salvesta"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notices list */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : sortedNotices.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center text-slate-400">
              <Megaphone className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Teadetetahvel on tühi</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {sortedNotices.map(notice => (
              <Card
                key={notice.notice_id}
                className={`border-slate-200 group ${notice.pinned ? "border-l-4 border-l-amber-400" : ""}`}
                data-testid={`notice-${notice.notice_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {notice.pinned && <Pin className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
                        <h3 className="font-semibold text-slate-900 text-sm">{notice.title}</h3>
                      </div>
                      {notice.text && (
                        <p className="text-sm text-slate-600 whitespace-pre-wrap mt-1">{notice.text}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                        <span>{notice.author_name}</span>
                        <span>{new Date(notice.created_at).toLocaleDateString("et-EE", { day: "numeric", month: "short", year: "numeric" })}</span>
                        {notice.edited_at && <span className="italic">muudetud</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => togglePin(notice)} className="p-1.5 rounded hover:bg-slate-100" data-testid={`pin-notice-${notice.notice_id}`}>
                        {notice.pinned ? <PinOff className="w-3.5 h-3.5 text-amber-500" /> : <Pin className="w-3.5 h-3.5 text-slate-400" />}
                      </button>
                      <button onClick={() => startEdit(notice)} className="p-1.5 rounded hover:bg-slate-100" data-testid={`edit-notice-${notice.notice_id}`}>
                        <Pencil className="w-3.5 h-3.5 text-slate-400" />
                      </button>
                      <button onClick={() => deleteNotice(notice.notice_id)} className="p-1.5 rounded hover:bg-red-50" data-testid={`delete-notice-${notice.notice_id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
      </div>
      <BottomNav />
    </div>
  );
}
